//@ts-ignore-all
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import CanvasView from "../canvas";
import { DashboardView } from "../dashboard";
import DiscoveryView from "../discovery";
import Forwarder from "../forward";
import InfoHubView from "../wiki/new-info-hub";

// Material components
import { useKeycloak } from "@react-keycloak/web";
import { activityAtom } from "../../stores/jotai/activities/activities";
import { artifactAtom } from "../../stores/jotai/artifacts/artifacts";
import {
  baseAtom,
  blockListedBase,
  checkRole,
  getFirstInterview
} from "../../stores/jotai/base";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import { interviewsAtom } from "../../stores/jotai/interviews/interviews";
import { useGetActivities } from "../../tanstackQuery/queries/activities";
import { useGetArtifacts } from "../../tanstackQuery/queries/artifacts";
import { useGetInterviews } from "../../tanstackQuery/queries/interviews";
import Home from "../Home/Home";
import LicenseVerify from "../route-verify/license.verify";
import NotFoundPage from "../not-found";
import Scoring from "../scoring/Scoring";
import InsightPage from "../../components/insights";
import SuperAdminView from "../super-admin/pages/SuperAdminView";
import ManageOrg from "../super-admin/pages/ManageOrg";
import ManageCohort from "../super-admin/pages/ManageCohort";
import ManageTeam from "../super-admin/pages/ManageTeam";
import UserDetails from "../../components/super-admin/systemManagement/userDetails/UserDetails";
import SuperAdminVerify from "../route-verify/s-admin-verify";

export function CustomRoute() {
  const [base, setBase] = useAtom(baseAtom);
  const [discoverySession] = useAtom(discoverySessionAtom);
  const isInstructor = checkRole(base, "isInstructor");
  const { keycloak } = useKeycloak();
  const useableBase = blockListedBase(base, ["uppyFiles"]);
  const setAllInterview = useSetAtom(interviewsAtom);
  const setArtifactData = useSetAtom(artifactAtom);
  const setActivities = useSetAtom(activityAtom);

  const location = useLocation();
  const groupID = base?.team?.groupID;
  const sessionID = discoverySession?.sessionID;

  const renderRoutesProps = useCallback(() => {
    return {
      ...useableBase,
      baseAtomBridge: useableBase,
      discoverySession: discoverySession,
      setBase: setBase,
      appState: useableBase ? useableBase?.app : {},
      activityFeed: useableBase?.activity
    };
  }, [useableBase, setBase, discoverySession]);

  const subRouteDataPackage = renderRoutesProps();

  const { data: interviews, isLoading } = useGetInterviews(
    { accessToken: keycloak?.token },
    { groupID },
    {
      enabled: !!groupID
    }
  );

  const { data: artifactDetails, isLoading: artifactLoading } = useGetArtifacts(
    { accessToken: keycloak?.token },
    { groupID, sessionID },
    {
      enabled: !!groupID
    }
  );

  const { data: activitiesData, isLoading: isActivityLoading } =
    useGetActivities(
      { accessToken: keycloak?.token },
      { groupID, sessionID },
      { enabled: !!(groupID && sessionID) }
    );

  useEffect(() => {
    if (interviews) {
      setAllInterview((prev) => {
        if (interviews?.length) {
          return [...interviews];
        } else {
          return [];
        }
      });
    }

    try {
      if (base.selectedInterview === undefined && interviews?.length) {
        setBase((prev) => {
          return {
            ...prev,
            selectedInterview: getFirstInterview(
              interviews,
              prev?.discoveryViewMode
            )
          };
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, [interviews, isLoading, base?.team, base?.org]);

  useEffect(() => {
    if (artifactDetails) {
      setArtifactData(artifactDetails);
    }
  }, [artifactDetails, artifactLoading]);

  useEffect(() => {
    if (activitiesData?.activities) {
      setActivities(activitiesData?.activities);
    }

    try {
      if (activitiesData) {
        setBase((prev) => {
          return {
            ...prev,
            canvasSliderValue: activitiesData?.activities?.length
          };
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, [activitiesData, isActivityLoading]);

  return (
    <Routes>
      <Route element={<LicenseVerify base={base} setBase={setBase} />}>
        <Route
          path="/"
          element={
            isInstructor ? (
              <DashboardView
                {...subRouteDataPackage}
                isHomeCardVisible={true}
              />
            ) : (
              <Home {...subRouteDataPackage} />
            )
          }
        />
        <Route
          path="/home"
          element={
            isInstructor ? (
              <DashboardView
                {...subRouteDataPackage}
                isHomeCardVisible={true}
              />
            ) : (
              <Home {...subRouteDataPackage} />
            )
          }
        />
        <Route
          path="/canvas"
          element={<CanvasView {...subRouteDataPackage} />}
        />
        <Route
          path="/canvas/:hypothesisID"
          element={<CanvasView {...subRouteDataPackage} />}
        />
        <Route
          path="/canvas/comment/:entryID/:chatID"
          element={<CanvasView {...subRouteDataPackage} />}
        />
        <Route
          path="discovery"
          element={<DiscoveryView {...subRouteDataPackage} />}
        />
        <Route
          path="discovery/:interviewID"
          element={<DiscoveryView {...subRouteDataPackage} />}
        />
        <Route
          path="discovery/:interviewID/:artifactID"
          element={<DiscoveryView {...subRouteDataPackage} />}
        />
        <Route
          path="discovery/:interviewID/:artifactID/:findingID"
          element={<DiscoveryView {...subRouteDataPackage} />}
        />
        <Route
          path="/dashboard"
          element={
            <DashboardView {...subRouteDataPackage} isHomeCardVisible={false} />
          }
        />
        <Route
          path="/course-videos"
          element={<InfoHubView {...subRouteDataPackage} />}
        />
        <Route
          path="/course-videos/:id"
          element={<InfoHubView {...subRouteDataPackage} />}
        />
        <Route
          path="/information-hub"
          element={<InfoHubView {...subRouteDataPackage} />}
        />
        <Route
          path="/new-info-hub"
          element={<InfoHubView {...subRouteDataPackage} />}
        />
        {/* <Route path="/team-information-hub" element={<TeamInformationHubView {...subRouteDataPackage} />} /> */}
        {/* <Route path="/billing" element={<BillingContainer {...subRouteDataPackage} />} /> */}
        {/* <Route path="/insights" element={withInsightHandlers(InsightsContainer)({ ...subRouteDataPackage })} /> */}
        <Route path="/insights" element={<InsightPage />} />

        {/* <Route path="/insights" element={<InsightPage/>} /> */}
        {/* <Route path="/search" element={<SearchContainer {...subRouteDataPackage} />} /> */}
        <Route
          path="/presentation"
          element={<Scoring {...subRouteDataPackage} />}
        />
        {/* <Route path="/presentation-links" element={<PresentationLinks {...subRouteDataPackage} />} /> */}
        <Route
          path="/forward/*"
          element={<Forwarder {...subRouteDataPackage} location={location} />}
        />
      </Route>
      <Route element={<SuperAdminVerify base={base} />}>
        <Route
          path="admin"
          element={<SuperAdminView {...subRouteDataPackage} />}
        ></Route>
        <Route
          path="admin/:userId"
          element={<UserDetails {...subRouteDataPackage} />}
        />
        <Route
          path="admin/cohort/:cohortID/team/:teamID"
          element={<ManageTeam {...subRouteDataPackage} />}
        />
        <Route
          path="admin/cohort/:groupID"
          element={<ManageCohort {...subRouteDataPackage} />}
        />
        <Route
          path="admin/org/:orgID"
          element={<ManageOrg {...subRouteDataPackage} />}
        />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
